import { Injectable } from '@angular/core';
import { firstValueFrom, forkJoin, from, map } from 'rxjs';
import { ApiListResponse } from 'src/app/api/api';
import { ApiSearch } from 'src/app/api/services/api-utils.service';
import type { Folder, FolderItem } from 'src/app/api/services/folder';
import { FoldersApiService } from 'src/app/api/services/folders-api.service';

export type FolderTreeItem = {
  id: string;
  name: string;
  parentId?: string;
  children: FolderTreeItem[];
};

@Injectable({
  providedIn: 'root',
})
export class FolderService {
  folderTree: FolderTreeItem[] = [];
  folderPathTree: FolderTreeItem[] = [];
  folderMap: Map<string, FolderTreeItem> = new Map();

  constructor(private foldersApiService: FoldersApiService) {}

  async loadFolders(type: string, options: ApiSearch): Promise<FolderItem[]> {
    const { query, includeSubcompanies, sortBy, reverse } = options;
    const nameQuery = query ? ` AND name:~"${query}"` : '';
    const parentQuery =
      !query && options.folderId !== undefined
        ? ` AND parentId:"${options.folderId || FoldersApiService.ROOT_FOLDER_ID}"`
        : '';
    const folderQuery = `kind:~"${type}"${nameQuery}${parentQuery}`;
    const folders: Folder[] = [];
    const items: FolderItem[] = [];
    let cursor: null | string = null;

    do {
      const response = await this.foldersApiService.list(
        {
          query: folderQuery,
          includeSubcompanies,
          sortBy: sortBy,
          reverse: reverse,
        },
        cursor,
      );

      folders.push(...response.items);
      items.push(
        ...response.items.map(
          (item) =>
            ({
              ...item,
              isFolder: true,
            }) as FolderItem,
        ),
      );
      cursor = response.cursor || response.nextPageToken;
    } while (cursor);
    if (!query && options.folderId === undefined) {
      this.loadTree(folders);
    }
    return items;
  }

  loadTree(folders: Folder[]): void {
    this.folderTree = [];
    this.folderMap.clear();

    // First pass: create map of folder items
    folders.forEach((folder) => {
      if (folder.id) {
        const folderItem: FolderTreeItem = {
          id: folder.id,
          name: folder.name,
          parentId: folder.parentId,
          children: [],
        };
        this.folderMap.set(folder.id, folderItem);
      }
    });

    // Second pass: build tree structure
    for (const folderItem of this.folderMap.values()) {
      if (!folderItem.parentId) {
        this.folderTree.push(folderItem);
      } else {
        const parentItem = this.folderMap.get(folderItem.parentId);
        if (parentItem) {
          parentItem.children.push(folderItem);
        } else {
          this.folderTree.push(folderItem);
        }
      }
    }
  }

  async loadFolderPath(folderId: string | null | undefined): Promise<void> {
    try {
      const folders = await this.getFolderPath(folderId);
      this.folderPathTree = this.buildFolderPathTree(folders);
    } catch (error) {
      console.debug('Failed to load folder path.', error);
      this.folderPathTree = [];
    }
  }

  deleteFolder(folderId: string) {
    return this.foldersApiService.delete(folderId);
  }

  showFolderItem(
    folderId: string | null | undefined,
    itemFolderId: string | null | undefined,
  ): boolean {
    return (!folderId && !itemFolderId) || folderId === itemFolderId;
  }

  async getItemsFromFolderAndSubfolders<T extends { id?: string }>(
    folderId: string,
    service: HasFolderStructure<T>,
  ): Promise<T[]> {
    const subfolders = await this.getSubfolderIds(folderId);
    const folders = [folderId, ...subfolders];

    return firstValueFrom(
      forkJoin(
        folders.map((folderId) => from(service.listByFolderId(folderId))),
      ).pipe(
        map((responses) => responses.flatMap((response) => response.items)),
      ),
    );
  }

  private async getSubfolderIds(folderId: string): Promise<string[]> {
    const folder = this.findFolderRecursive(this.folderTree, folderId);
    return folder
      ? this.getSubfoldersIds(folder, [])
      : this.loadSubfolderIds(folderId);
  }

  private async loadSubfolderIds(folderId: string): Promise<string[]> {
    const folder = (await this.foldersApiService.get(folderId)).item;
    if (!folder?.kind) {
      return [];
    }

    const children = await this.loadFolders(folder.kind, {
      query: '',
      folderId,
    });
    const descendantIds = await Promise.all(
      children.map((child) => this.loadSubfolderIds(child.id)),
    );

    return children.flatMap((child, index) => [
      child.id,
      ...descendantIds[index],
    ]);
  }

  private getSubfoldersIds(
    currentFolder: FolderTreeItem,
    folders: string[],
  ): string[] {
    currentFolder.children.forEach((child) => {
      folders.push(child.id);
      if (child.children.length > 0) {
        this.getSubfoldersIds(child, folders);
      }
    });
    return folders;
  }

  private findFolderRecursive(
    folders: FolderTreeItem[],
    folderId: string,
  ): FolderTreeItem | null {
    for (const folder of folders) {
      if (folder.id === folderId) {
        return folder;
      }
      const found = this.findFolderRecursive(folder.children, folderId);
      if (found) {
        return found;
      }
    }
    return null;
  }

  private buildFolderPathTree(folders: FolderTreeItem[]): FolderTreeItem[] {
    for (let i = 0; i < folders.length - 1; i++) {
      folders[i].children = [folders[i + 1]];
    }

    return folders.length ? [folders[0]] : [];
  }

  private async getFolderPath(
    folderId: string | null | undefined,
    visitedFolderIds = new Set<string>(),
  ): Promise<FolderTreeItem[]> {
    if (
      !folderId ||
      folderId === FoldersApiService.ROOT_FOLDER_ID ||
      visitedFolderIds.has(folderId)
    ) {
      return [];
    }

    visitedFolderIds.add(folderId);
    const folder = (await this.foldersApiService.get(folderId)).item;
    if (!folder?.id) {
      return [];
    }

    return [
      ...(await this.getFolderPath(folder.parentId, visitedFolderIds)),
      this.toFolderTreeItem(folder),
    ];
  }

  private toFolderTreeItem(folder: Folder): FolderTreeItem {
    return {
      id: folder.id,
      name: folder.name,
      parentId: folder.parentId,
      children: [],
    };
  }
}

export interface HasFolderStructure<T> {
  listByFolderId: (folderId: string) => Promise<ApiListResponse<T>>;
}
